import {createSlice} from '@reduxjs/toolkit';
import _ from 'lodash';
import moment from 'moment';
import API from '../../app/api';
import {
    checkRequestObj,
    INITIAL_FORM_STATE as DEFAULT_REQUEST_STATE,
    mapWidgetsFromAPI,
    mapWidgetsToAPI,
} from '../../pages/bedManagement/bmUtils';
import Formatter from '../../util/formatter';
import Notes from '../../util/notes';
import {setAgyleHeaderLoading, setSnackbar} from './globals';
import {addNoteForPatient} from './notes';
import {saveAgylePreference} from './preferences';
import {getWardsIdForPatientsSearch} from './wardMap';
import {CLINICAL_CONCLUSION_INNER_STATE} from '../../pages/ward/forms/formsUtil';

const DEFAULT_BM_PREFS = {
    widgets: [],
    columnPreferences: null,
    mainScreenWidgets: null,
    tableColumns: {
        mainTable: [],
        mainTableSticky: 5,
        bedTable: [],
        bedTableSticky: {
            bedStatus: 6,
            siderooms: 6,
            breaches: 4,
            outliers: 5,
            offWard: 9,
            porterView: 6,
            cleaningRequest: 4,
        },
    },
};

const DEFAULT_PAGE_SIZE = 20;

const INITIAL_BED_REQUEST_STATE = {
    requestScreenMode: 'request', // Available: request | timeline
    secondaryBedRequest: false,
    sideroomRequest: false,
    ready: false,
    chosenBed: null,
    request: null,
};

const INITIAL_MODALS_STATE = {
    allocateBed: false,
    bedDetails: false,
    breachWarning: true,
    cancelAllocation: false,
    cancelBedRequest: false,
    sendNotification: false,
    acknowledgeRequest: false,
    acknowledgeAllRequests: false,
    requestScreen: false,
    autoCheckBox: false,
};

const INITIAL_FLAGS = {
    isSavingWidgetPrefs: false,
};

export const SIZE_MAP = {
    small: 1,
    medium: 2,
    large: 3,
};

export const bedManagement = createSlice({
    name: 'bedManagement',
    initialState: {
        loading: false,
        prowizorkaRefreshData: false,
        specialtiesDict: [],
        bmData: [],
        bmDataCompleted: {
            data: [],
            type: null,
        },
        bmWidgets: [],
        bmFlags: INITIAL_FLAGS,
        requestsToAcknowledgeAll: [],
        tcis: [],
        viewType: 'view',
        panelType: 'action',
        contextType: 'patient',
        settingsOpen: false,
        editableWidgets: false,
        bedAllocateChosenPatient: null,
        chosenPatientAdditionalData: null,
        editableRequestObj: DEFAULT_REQUEST_STATE,
        wardId: null,
        facilityId: null,
        destination: false,
        wardBeds: [],
        hydratedBeds: [],
        bedStatuses: {},
        patientTableFilter: {
            patientType: 'all',
            age: [],
            spec: [],
            divSpec: [],
            bedStatus: 'closed',
            offWard: 'all',
            bedGenderRestriction: 'all',
        },
        bedTableFilter: {
            patientType: 'all',
            age: [],
            spec: [],
            divSpec: [],
            bedStatus: 'all',
            offWard: 'all',
            bedGenderRestriction: 'all',
            dischargeDate: null,
        },
        openModals: INITIAL_MODALS_STATE,
        bedRequest: INITIAL_BED_REQUEST_STATE,
        bedOccupied: [],
        mapOtherWardBedStatus: null,
        mapViewFilters: {
            map: null,
            zone: null,
            sr: false,
            requested: false,
            allocated: false,
            ptReady: false,
            bedReady: false,
            bmMapSearch: '',
        },
    },
    reducers: {
        setBMLoading: (state, data) => {
            state.loading = data.payload;
        },
        setProwizorkaRefreshData: (state, data) => {
            state.prowizorkaRefreshData = data.payload;
        },
        setBMData: (state, data) => {
            state.bmData = data.payload;
        },
        setBMDataCompleted: (state, data) => {
            state.bmDataCompleted = data.payload;
        },
        setBMWidgets: (state, data) => {
            state.bmWidgets = data.payload;
        },
        setBMFlags: (state, data) => {
            state.bmFlags = data.payload;
        },
        setRequestsToAcknowledgeAll: (state, data) => {
            state.requestsToAcknowledgeAll = data.payload;
        },
        setSpecialtiesDict: (state, data) => {
            state.specialtiesDict = data.payload;
        },
        setTCIs: (state, data) => {
            state.tcis = data.payload;
        },
        setView: (state, data) => {
            state.viewType = data.payload;
        },
        setPanelType: (state, data) => {
            state.panelType = data.payload;
        },
        setContextType: (state, data) => {
            state.contextType = data.payload;
        },
        setPatientTableFilter: (state, data) => {
            state.patientTableFilter = {
                ...state.patientTableFilter,
                ...data.payload,
            };
        },
        setBedTableFilter: (state, data) => {
            state.bedTableFilter = {
                ...state.bedTableFilter,
                ...data.payload,
            };
        },
        setSettingsOpen: (state, data) => {
            state.settingsOpen = data.payload;
        },
        setEditableWidgets: (state, data) => {
            state.editableWidgets = data.payload;
        },
        setBedRequest: (state, data) => {
            state.bedRequest = {
                ...state.bedRequest,
                ...data.payload,
            };
        },
        setBedAllocateChosenPatient: (state, data) => {
            state.bedAllocateChosenPatient = data.payload.patient;
            state.editableRequestObj = data.payload.request;
        },
        setChosenPatientAdditionalData: (state, data) => {
            state.chosenPatientAdditionalData = data.payload;
        },
        setEditableRequestObject: (state, data) => {
            state.editableRequestObj = data.payload;
        },
        setOpenModalState: (state, data) => {
            state.openModals = {
                ...state.openModals,
                ...data.payload,
            };
        },
        setIsDestination: (state, data) => {
            state.destination = data.payload;
        },
        setWardId: (state, data) => {
            state.wardId = data.payload;
        },
        setFacilityId: (state, data) => {
            state.facilityId = data.payload;
        },
        setWardBeds: (state, data) => {
            state.wardBeds = data.payload;
        },
        setWardBedsHydrated: (state, data) => {
            state.hydratedBeds = data.payload;
        },
        setBedStatuses: (state, data) => {
            state.bedStatuses = data.payload;
        },
        setBedOccupied: (state, data) => {
            state.bedOccupied = data.payload;
        },
        setMapOtherWardBedStatus: (state, data) => {
            state.mapOtherWardBedStatus = data.payload;
        },
        setMapViewFilters: (state, data) => {
            state.mapViewFilters = {
                ...state.mapViewFilters,
                ...data.payload,
            };
        },
    },
});

// Setters
export const {
    setBMLoading,
    setProwizorkaRefreshData,
    setBMData,
    setBMWidgets,
    setBMFlags,
    setBMDataCompleted,
    setRequestsToAcknowledgeAll,
    setSpecialtiesDict,
    setTCIs,
    setView,
    setPanelType,
    setContextType,
    setPatientTableFilter,
    setBedTableFilter,
    setSettingsOpen,
    setEditableWidgets,
    setBedRequest,
    setBedAllocateChosenPatient,
    setChosenPatientAdditionalData,
    setOpenModalState,
    setIsDestination,
    setWardId,
    setFacilityId,
    setWardBeds,
    setWardBedsHydrated,
    setBedStatuses,
    setEditableRequestObject,
    setBedOccupied,
    setMapViewFilters,
    setMapOtherWardBedStatus,
} = bedManagement.actions;

// Variable
export const BMData = state => state.bedManagement.bmData;
export const BMLoading = state => state.bedManagement?.loading;
export const BMDataCompleted = state => state.bedManagement.bmDataCompleted;
export const BMRequestsToAcknowledgeAll = state => state.bedManagement.requestsToAcknowledgeAll;
export const BMSpecialtiesDict = state => state.bedManagement.specialtiesDict;
export const BMTCI = state => state.bedManagement.tcis;
export const BMViewType = state => state.bedManagement.viewType;
export const BMPanelType = state => state.bedManagement.panelType;
export const BMContextType = state => state.bedManagement.contextType;
export const BMPatientTableFilter = state => state.bedManagement.patientTableFilter;
export const BMBedTableFilter = state => state.bedManagement.bedTableFilter;
export const BMWidgets = state => state.bedManagement.bmWidgets;
export const BMFlags = state => state.bedManagement.bmFlags;
export const BMIsWidgetsEditable = state => state.bedManagement.editableWidgets;
export const BMIsSettingsOpen = state => state.bedManagement.settingsOpen;
export const BMBedRequest = state => state.bedManagement.bedRequest;
export const BMBedAllocateChosenPatient = state => state.bedManagement.bedAllocateChosenPatient;
export const BMChosenPatientAdditionalData = state => state.bedManagement.chosenPatientAdditionalData;
export const BMEditableRequest = state => state.bedManagement.editableRequestObj;
export const BMOpenModals = state => state.bedManagement.openModals;
export const BMDestination = state => state.bedManagement.destination;
export const BMWardId = state => state.bedManagement.wardId;
export const BMFacilityId = state => state.bedManagement.facilityId;
export const BMWardBeds = state => state.bedManagement.wardBeds;
export const BMWardBedsHydrated = state => state.bedManagement.hydratedBeds;
export const BMBedStatuses = state => state.bedManagement.bedStatuses;
export const BMMapViewFilters = state => state.bedManagement.mapViewFilters;
export const BMBedOccupied = state => state.bedManagement.bedOccupied;
export const BMProwizorkaRefreshData = state => state.bedManagement.prowizorkaRefreshData;
export const BMMapOtherWardBedStatus = state => state.bedManagement.mapOtherWardBedStatus;

export const getSpecialtiesDict = (dict) => (dispatch) => {
    if (!_.isEmpty(dict)) {
        dispatch(setSpecialtiesDict(dict));
    }
};

export const getSpecialityObject = (specCode) => (dispatch, getState) => {
    const dict = getState().bedManagement.specialtiesDict;
    return _.find(dict, {mainSpecialty: specCode});
};

export const loadAdditionalPatientData = (patientId) => (dispatch) => {
    API.getPatientAdditionalData(patientId).then(data => {
        if (data) {
            dispatch(setChosenPatientAdditionalData({...data.other}));
        }
    }).catch(function (error) {
        console.error(error);
    });
};

const sleep = m => new Promise(r => setTimeout(r, m));

export const setRefreshFalse = () => (dispatch) => {
    dispatch(setProwizorkaRefreshData(false));
};

export const setRefreshTrue = () => async (dispatch) => {
    try {
        await sleep(2000);
        dispatch(setProwizorkaRefreshData(true));
    } catch (e) {
        console.log(e);
    } finally {
        dispatch(setRefreshFalse());
    }
};

export const setFlag = (flag, value) => (dispatch) => {
    dispatch(setBMFlags({
        [flag]: value,
    }));
};

const prepareBRData = async (bedRequests, isMultiHospitalMode, dispatch) => {
    const onlyBR = _.chain(bedRequests)
        .map('bedRequests')
        .flatten()
        .value();
    const wardsIdToChecked = _.chain(onlyBR)
        .map('destination.wardId')
        .filter(wardId => !_.isEmpty(wardId))
        .uniq()
        .value();
    const bedIds = _.chain(onlyBR)
        .map('destination.bedId')
        .filter(wardId => !_.isEmpty(wardId))
        .uniq()
        .value();

    const wardsIdToCheckedBedStatuses = [];
    _.forEach(onlyBR, br => {
        if (br?.destination) {
            let wardsId = br?.destination?.wardId;
            if (isMultiHospitalMode && wardsId) {
                wardsId = dispatch(getWardsIdForPatientsSearch([wardsId], br?.destination?.hospitalId));
            }
            if (wardsId && !wardsIdToCheckedBedStatuses.includes(_.isArray(wardsId) ? wardsId[0] : wardsId)) {
                wardsIdToCheckedBedStatuses.push(_.isArray(wardsId) ? wardsId[0] : wardsId);
            }
        }
    })

    let data = {
        statuses: [],
        occupies: [],
    };

    // Get all bed statuses for a given ward
    await API.getWardsData(wardsIdToCheckedBedStatuses)
        .then(stats => {
            data.statuses = _.chain(stats)
                .filter(obj => obj.bedStatuses && !_.isEmpty(obj.bedStatuses))
                .flatMap(obj => _.toPairs(obj.bedStatuses))
                .reduce((acc, [key, value]) => {
                    acc[key] = value;
                    return acc;
                }, {})
                .value();
        });

    // Checking if the given beds are occupied
    await API.getWardsAttendances(wardsIdToChecked)
        .then(pts => {
            data.occupies = _.chain(bedIds)
                .filter(str => _.some(pts, obj => obj.bed === str))
                .value();
        });
    return data;
};

export const prepareDataBM = (data, noDispatch = false, tciList = false, completed = false) => async (dispatch, getState) => {
    const dataToSave = [];
    if (data && data?.length > 0) {
        const isMultiHospitalMode = getState().ward.isMultiHospitalMode;
        const dataToUsed = await prepareBRData(data, isMultiHospitalMode, dispatch);
        for (const br of data) {
            if (!_.isEmpty(br?.bedRequests)) {
                let request = _.find(br.bedRequests, request => request?.status === 'NEW' && !request?.followUp);
                const followUp = _.find(br.bedRequests, request => request?.status === 'NEW' && request?.followUp);
                if (completed) request = _.find(br.bedRequests, request => request?.status !== 'NEW' && !request?.followUp);
                let bedReadyStatus = null;
                let bedReadyStatusFollowUp = null;

                // Check bed status for the main bed request
                if (request?.allocated?.checked && request?.destination?.bedId) {
                    let isOccupied = dataToUsed?.occupies?.includes(request.destination.bedId);
                    let haveStatus = false;
                    let isCleaning = false;
                    if (!isOccupied) {
                        const bedStatus = dataToUsed?.statuses?.[request.destination.bedId];
                        haveStatus = !!bedStatus;
                        isCleaning = bedStatus?.status === 'cleaning' || bedStatus?.status === 'cleaningRequest';
                    }

                    if (isOccupied) bedReadyStatus = 'Occupied';
                    else if (haveStatus) bedReadyStatus = isCleaning ? 'Cleaning' : 'Blocked';
                    else bedReadyStatus = 'Ready';
                }

                // Check bed status for follow-up bed request.
                if (followUp?.allocated?.checked && followUp?.destination?.bedId) {
                    let isOccupied = dataToUsed?.occupies?.includes(followUp.destination.bedId);
                    let haveStatus = false;
                    let isCleaning = false;
                    if (!isOccupied) {
                        const bedStatus = dataToUsed?.statuses?.[followUp.destination.bedId];
                        haveStatus = !!bedStatus;
                        isCleaning = bedStatus?.status === 'cleaning' || bedStatus?.status === 'cleaningRequest';
                    }

                    if (isOccupied) bedReadyStatus = 'Occupied';
                    else if (haveStatus) bedReadyStatus = isCleaning ? 'Cleaning' : 'Blocked';
                    else bedReadyStatus = 'Ready';
                }

                dataToSave.push({
                    ...br,
                    singleBedRequest: request || null,
                    followUpBedRequest: followUp || null,
                    bedReadyStatus,
                    bedReadyStatusFollowUp,
                });
            }

            if (tciList && _.isEmpty(br?.bedRequests)) {
                dataToSave.push({
                    ...br,
                    singleBedRequest: null,
                    followUpBedRequest: null,
                    bedReadyStatus: null,
                    bedReadyStatusFollowUp: null,
                });
            }
        }
    }
    if (noDispatch) {
        return dataToSave;
    } else {
        dispatch(setBMData(dataToSave));
    }
};

export const loadCompletedRequests = () => async (dispatch) => {
    const dataToSave = {
        data: [],
        type: 'week',
    };

    const data = {
        from: moment().subtract(1, 'week').format('YYYY-MM-DD'),
        completed: true,
    };

    await API.getPatientsWithBedRequest(data)
        .then(res => {
            if (_.isArray(res?.__data)) {
                dataToSave.data = dispatch(prepareDataBM(res.__data, true, false, true));
            }
        })
        .catch(err => {
            console.error(err);
            dispatch(setSnackbar({type: 'error', message: 'Cannot load bed requests.'}));
        });
    dispatch(setBMDataCompleted(dataToSave));
};

export const getBMData = (noDestination = false) => async (dispatch, getState) => {
    dispatch(setAgyleHeaderLoading(true));
    // For multi hospital
    const isMultiHospitalMode = getState().ward.isMultiHospitalMode;
    const hospitalId = getState().bedManagement.facilityId;

    const wardId = getState().bedManagement.wardId;
    const destination = getState().bedManagement.destination;
    const obj = {
        wardIds: [wardId],
        destination: destination,
    };

    if (!wardId) {
        delete obj.wardIds;
    }

    if (noDestination || !wardId) {
        delete obj.destination;
    }

    const dataObj = [];

    await API.getPatientsWithBedRequest(obj)
        .then(res => {
            if (_.isArray(res?.__data)) {
                if (isMultiHospitalMode && hospitalId) {
                    const filter = _.filter(res?.__data, {facility: hospitalId});
                    dataObj.push(...filter);
                } else {
                    dataObj.push(...res?.__data);
                }
            }
        })
        .catch(err => {
            console.error(err);
            dispatch(setSnackbar({type: 'error', message: 'Cannot load bed requests.'}));
        });

    const allBedRequest = [];

    await API.getBedRequest(`status eq 'NEW'`)
        .then(res => {
            if (!_.isEmpty(res?.__data)) {
                allBedRequest.push(...res.__data);
            }
        })
        .catch(err => {
            console.error(err);
            dispatch(setSnackbar({type: 'error', message: 'Cannot load all bed requests.'}));
        });

    await API.getPatientAttendanceTCI(wardId)
        .then(res => {
            createFakePiWForBM(dataObj, res, allBedRequest, dispatch);
        })
        .catch(err => {
            console.error(err);
            dispatch(setSnackbar({type: 'error', message: 'Cannot load TCI attendances.'}));
        });
    dispatch(loadCompletedRequests());
    dispatch(prepareDataBM(_.filter(dataObj, br => br.__id !== null)));
    dispatch(setAgyleHeaderLoading(false));
};

// Very cool feature
// Loads future attendances to check if they have a bed request and put in list a fake PatientInWards object, because future attendances don't have it.
// Maybe I'll regret it
const createFakePiWForBM = (brList, tciAtt, bedRequests, dispatch) => {
    const listOfTCI = [];
    if (!_.isEmpty(tciAtt)) {
        const fakePiWTemplate = {
            admission: null,
            bedRequests: [],
            patient: null,
            __id: Math.random(),
        };

        _.forEach(tciAtt, att => {
            let piw = _.cloneDeep(fakePiWTemplate);
            piw.admission = att;
            piw.admission.attendanceType = 'TCI';
            piw.patient = att?.patient;
            delete piw.admission.patient;

            const br = _.filter(bedRequests, b => b.patientId === piw?.patient?.__id && b.attendanceId === piw.admission?.__id);
            if (!_.isEmpty(br)) {
                piw.bedRequests.push(...br);
                brList.push(piw);
            }
            listOfTCI.push(piw);
        });
    }
    dispatch(setTCIs(dispatch(prepareDataBM(listOfTCI, true, true))));
};

export const getWardData = (wardId, beds, hydrated, returnValue = false) => async (dispatch, getState) => {
    let multiWards = getState().bedManagement?.bedStatuses;

    for (const arr of wardId) {
        await API.getWardData(arr).then(stats => {
            const multiHospital = _.startsWith(stats.wardId, 'ED##');
            multiWards = {
                ...multiWards,
                [stats.wardId]: stats.bedStatuses,
            };
            if (multiHospital) {
                multiWards = {
                    ...multiWards,
                    [stats.wardId?.replace('ED##', '')]: stats,
                };
            }
        });
    }

    if (returnValue) {
        return ({
            bedStatuses: multiWards,
            beds: beds,
            hydrated: hydrated,
        });
    }

    dispatch(setBedStatuses(multiWards));
    dispatch(setWardBeds(beds));

    if (hydrated) {
        dispatch(setWardBedsHydrated(hydrated));
    }
};

export const resetEditableReq = () => (dispatch) => {
    dispatch(setEditableRequestObject(DEFAULT_REQUEST_STATE));
};

export const changeBMRequestScreenMode = (mode) => (dispatch) => {
    dispatch(setBedRequest({requestScreenMode: mode}));
};

export const allocateBedToPatient = (bed) => (dispatch) => {
    dispatch(setBedRequest({
        chosenBed: bed,
    }));
};

export const openModal = (modalName, autoCheck = false) => (dispatch) => {
    dispatch(setOpenModalState({
        [modalName]: true,
        autoCheckBox: autoCheck,
    }));
};

export const closeModal = (modalName) => (dispatch) => {
    dispatch(setOpenModalState({
        [modalName]: false,
    }));
};

export const getWard = (ward) => (dispatch, getState) => {
    const parsedWardId = _.isObject(ward) ? ward?.id : ward;
    const validWard = _.find(getState().ward.wards, (wrd) => wrd?.wardsId.includes(parsedWardId));
    return validWard || null;
};

const handleCheckClinicalConclusion = (patient) => (dispatch, getState) => {
    const userObject = getState()?.user?.userData;
    const userFullName = Formatter.formatUserFullName(userObject);
    const newFilter = 'deactivated ne true';
    API.getDictByURL(`/$dict/ClinicalConclusionClinicians?$filter=${encodeURIComponent(newFilter)}`)
        .then((dictData) => {
            let findCC = null;

            if (dictData) {
                for (let clinician of dictData.__data) {
                    if (clinician.userName === userObject?.userName) {
                        findCC = clinician;
                    }
                }
            }

            let clinicalConclusion = CLINICAL_CONCLUSION_INNER_STATE;
            clinicalConclusion.who = findCC || userFullName;
            clinicalConclusion.when = moment().toISOString();
            clinicalConclusion.login = userObject?.userName;
            clinicalConclusion.status = 'Admitted';
            clinicalConclusion.timestamp = moment().toISOString();

            const data = [{
                'clinicalConclusion': clinicalConclusion,
            }];

            API.updatePatientInWard(patient.__id, data)
                .then(res => {
                    if (res) {
                        dispatch(addNoteForPatient(patient?.patient?.__id, {
                            note: Notes.getClinicalConclusionNote(clinicalConclusion),
                            noteType: 'clinicalConclusion',
                            patientsInWardsRef: [patient.__id],
                            episodeId: patient?.admission?.consultantEpisodeId || null,
                        }, patient.__id));
                    }
                })
                .catch(error => {
                    console.error(error);
                    dispatch(setSnackbar({type: 'error', message: 'Data not saved.'}));
                });
        });
};

export const handleSubmitBedRequest = (patient, form, afterSaveFn) => (dispatch) => {
    let isSubmitted = false;
    const validateForm = () => {
        let isValid = true;
        if (isStatusInvalid(form.requested) || isStatusInvalid(form.canceled)) {
            isValid = false;
            dispatch(setSnackbar({type: 'warning', message: 'Status clinician and timestamp must be selected.'}));
        }
        return isValid;
    };

    const isStatusInvalid = (field) => {
        if (field?.checked) return _.isNil(field.when);
        return false;
    };

    if (validateForm()) {
        let data = _.cloneDeep(form);
        if (!data.patientId) data.patientId = patient?.patient?.__id;
        if (!data.attendanceId) data.attendanceId = patient?.admission?.__id;
        if (!data.visitNumber) data.visitNumber = patient?.admission?.attendanceId;
        if (!data.currentWardId) data.currentWardId = patient?.wardId;

        // clear object
        delete data.__href;
        delete data.__type;
        delete data.__version;
        delete data._createdBy;
        delete data._createdDate;

        API.saveBedRequest(data)
            .then((res) => {
                isSubmitted = true;
                dispatch(addNoteForPatient(patient?.patient.__id, {
                    note: Notes.getBedRequestExternalNote(res),
                    noteType: 'bedRequest',
                    patientsInWardsRef: [patient.__id],
                    episodeId: patient?.admission?.consultantEpisodeId || null,
                }, patient.__id));
                dispatch(setSnackbar({
                    type: 'success',
                    message: 'Bed Request saved.',
                }));

                if (data.patientReady.checked && !data.canceled.checked && patient?.clinicalConclusion?.status !== 'Admitted') {
                    dispatch(handleCheckClinicalConclusion(patient));
                }

                if (afterSaveFn) {
                    afterSaveFn();
                }
            })
            .catch(() => {
                dispatch(setSnackbar({
                    type: 'error',
                    message: 'There was a problem with saving bed request.',
                }));
            });
    }
    return isSubmitted;
};

export const updateBedStatus = (bed, status, selectedWard) => async (dispatch, getState) => {
    let statusesDataClone = _.cloneDeep(getState().bedManagement.bedStatuses);
    const wardStatuses = _.cloneDeep(statusesDataClone?.[selectedWard.value]) || {};

    let response;
    const wardId = bed?.wardId;
    const bedId = bed?.__id;
    const statusObj = {
        type: status.type,
        status: status.status,
    };

    if (status.status === 'ready') {
        delete wardStatuses[bedId];
    } else {
        wardStatuses[bedId] = statusObj;
    }

    response = await API.updateWardData(wardId, [{'bedStatuses': wardStatuses}]);
    if (response.data) {
        dispatch(setBedStatuses({[wardId]: wardStatuses}));
        dispatch(setSnackbar({type: 'success', message: 'Bed status was saved successfully.'}));
    } else {
        dispatch(setSnackbar({type: 'error', message: 'Bed status was not saved successfully!'}));
        console.error('error in network', response);
    }
};

export const updatePatientAsReady = (patient, clinicianDict) => async (dispatch, getState) => {
    const userObject = getState().user.userData;
    const bedRequest = patient.singleBedRequest;
    let isUpdated = false;
    if (!bedRequest) return isUpdated;

    const initialReadyStatus = bedRequest?.patientReady?.checked;
    const readyObj = checkRequestObj(bedRequest, 'patientReady', userObject, clinicianDict, bedRequest?.patientReady?.note, !bedRequest?.patientReady?.checked);

    try {
        if (readyObj?.patientReady?.checked !== initialReadyStatus) {
            dispatch(handleSubmitBedRequest(patient, readyObj));
            isUpdated = true;
        }
    } catch (err) {
        console.error(err);
        dispatch(setSnackbar({type: 'error', message: 'Bed request was not updated successfully!'}));
    } finally {
        return isUpdated;
    }
};

export const checkBookedBeds = (wardId, fromMap) => async (dispatch, getState) => {
    const isMultiHospitalMode = getState().ward.isMultiHospitalMode;
    const bedOccupied = [];
    const setWardId = new Set();
    await API.getBedRequest(`status eq 'NEW'`)
        .then(res => {
            if (!_.isEmpty(res?.__data)) {
                _.forEach(res.__data, br => {
                    // Check for allocated beds and filter if bed request is not canceled
                    if (br?.allocated?.checked && br?.destination && !br?.canceled?.checked) {
                        // If wardId is provided, then check destination
                        if ((!_.isEmpty(wardId) && wardId.includes(br?.destination?.wardId)) || _.isEmpty(wardId)) {
                            bedOccupied.push(br?.destination?.bedId);
                        }

                        // Get ale wards id to check bed status for requesting patients (check if bed is allocated)
                        if (fromMap && !_.isEmpty(wardId) && br?.destination?.bedId && wardId?.includes(br.currentWardId)) {
                            if (isMultiHospitalMode) {
                                let wardId = br?.destination?.wardId;
                                if (isMultiHospitalMode) {
                                    wardId = dispatch(getWardsIdForPatientsSearch([wardId], br?.destination?.hospitalId));
                                }
                                setWardId.add(_.isArray(wardId) ? wardId[0] : wardId);
                            } else {
                                setWardId.add(br?.destination?.wardId);
                            }
                        }
                    }
                });
            }
        })
        .catch(err => {
            console.error(err);
            dispatch(setSnackbar({type: 'error', message: 'Cannot load all bed requests.'}));
        });

    dispatch(setBedOccupied(bedOccupied));

    // Get all bed statuses from a set of wards id
    if (fromMap) {
        if (!_.isEmpty(setWardId)) {
            let wardStats = {};
            for (const id of setWardId) {
                await API.getWardData(id)
                    .then(stats => {
                        wardStats[id] = stats?.bedStatuses || [];
                        if (isMultiHospitalMode) {
                            wardStats[stats.id?.replace('ED##', '')] = stats?.bedStatuses || [];
                        }
                    });
            }
            dispatch(setMapOtherWardBedStatus(wardStats));
        } else {
            dispatch(setMapOtherWardBedStatus(null));
        }
    }
};

/**
 * Create data for bed context for bed management by ward and specific hospital in case of multi hospitals.
 *
 * @param {string} ward - Ward id
 * @param {string} hospital - Facility id
 * @param {number} page - Number of page
 * @returns {function(*, *): Promise<*[]>}
 */
export const getBedContextData = (ward, hospital, page) => async (dispatch, getState) => {
    const isMultiHospital = getState().ward.isMultiHospitalMode;
    const mapList = _.filter(getState().ward.wards, map => map.wardsId?.length === 1 && !map.archived);
    const bedsData = [];
    let occupied = [];
    let offWards = [];
    let allBeds = 0;

    // For multi-hospital mode, facility must be selected
    if (isMultiHospital && !hospital) {
        return null;
    }

    try {
        const wardsIdToChecked = _.chain(mapList)
            .map('wardsId')
            .flatten()
            .uniq()
            .value();

        // Downloading all necessary data. Zones/Beds/EscalationLevels/WardBedStatuses/Checking for Patients
        const el = await API.getEscalationLevels(hospital);
        const areas = await API.getAllAreas(ward)
            .then(r => _.filter(r?.data, zone => wardsIdToChecked.includes(zone?.wardId) && zone.hospitalId === hospital));
        const beds = await API.getAllBeds(ward)
            .then(r => {
                const sort = _.orderBy(r?.data, ['wardId', 'name'], ['asc', 'asc']);
                const start = (page - 1) * DEFAULT_PAGE_SIZE;
                const end = start + DEFAULT_PAGE_SIZE;
                let filtered = _.filter(sort, beds => wardsIdToChecked.includes(beds?.wardId) && !beds.archived && beds.type !== 'wlist' && beds.hospitalId === hospital);
                if (ward) {
                    filtered = _.filter(sort, beds => wardsIdToChecked.includes(beds?.wardId) && !beds.archived && beds.type !== 'wlist' && beds.hospitalId === hospital);
                }
                allBeds = filtered.length;

                if (page) return _.slice(filtered, start, end);
                else return filtered;
            });

        let wardsIdFromBeds = _.chain(beds)
            .map('wardId')
            .uniq()
            .value();

        const obj = {
            wardIds: wardsIdFromBeds,
            destination: false,
        };

        const bedRequests = await API.getPatientsWithBedRequest(obj)
            .then(res => res?.__data)
            .catch(err => {
                console.error(err);
                dispatch(setSnackbar({type: 'error', message: 'Cannot load bed requests.'}));
            });

        const ptInWards = await API.getWardsAttendances(wardsIdFromBeds)
            .then(r => {
                const ptArr = [];
                _.forEach(r, pt => {
                    if (pt) {
                        let elementHidden = !pt?.bed && pt?.admission?.locationDescription === 'DISCHARGE';
                        if (!elementHidden) ptArr.push(pt);
                    }
                });
                return ptArr;
            })
            .catch(err => {
                console.error(err);
                dispatch(setSnackbar({type: 'error', message: 'Cannot load patients.'}));
            });

        wardsIdFromBeds = dispatch(getWardsIdForPatientsSearch(wardsIdFromBeds, hospital));
        const statuses = await API.getWardsData(wardsIdFromBeds)
            .then(stats => _.chain(stats)
                .filter(obj => obj.bedStatuses && !_.isEmpty(obj.bedStatuses))
                .flatMap(obj => _.toPairs(obj.bedStatuses))
                .reduce((acc, [key, value]) => {
                    acc[key] = value;
                    return acc;
                }, {})
                .value(),
            );

        // Checking if the given beds are occupied
        await API.getWardsAttendances(wardsIdFromBeds).then(pts => {
                occupied = _.chain(beds.map(bed => bed?.__id))
                    .filter(str => _.some(pts, obj => obj.bed === str))
                    .value();
                offWards = _.chain(pts)
                    .filter(pts => pts?.offWard && !pts.offWard?.completed)
                    .value();
            },
        );

        _.forEach(beds, bed => {
            const escalation = _.find(el, (e) => e.wardId === bed?.wardId);
            if (_checkEscalation(bed, escalation)) {
                let obj = {...bed};
                const zone = _.find(areas, (zone) => _.includes(zone?.linkedBeds, bed?.__id));
                const ward = _.find(mapList, (map) => map.wardsId[0] === bed?.wardId && map.hospitalId === hospital);
                const ptBedRequest = _.find(bedRequests, obj => {
                    return _.some(obj.bedRequests, request => {
                        return _.get(request, 'destination.bedId') === bed?.__id;
                    });
                });
                const ptOccupied = _.find(ptInWards, {bed: bed?.__id});
                const offPt = _.find(offWards, {bed: bed?.__id});

                obj['zone'] = zone?.name || 'Unused';
                obj['wardName'] = ward?.name || '';
                obj['patient'] = ptBedRequest || null;
                obj['patientOccupied'] = ptOccupied || null;
                obj['offWardPatient'] = offPt || null;
                obj['status'] = _getBedStatus(bed, occupied, statuses, ptBedRequest, !!zone);
                obj['inCleaning'] = statuses?.[bed?.__id]?.status === 'cleaning' || statuses?.[bed?.__id]?.status === 'cleaningRequest';
                if (zone) bedsData.push(obj);
            }
        });

        const totalPages = Math.ceil(allBeds / DEFAULT_PAGE_SIZE);
        return {
            data: _.orderBy(bedsData, ['wardName', 'name'], ['asc', 'asc']),
            page,
            pages: totalPages,
        };
    } catch (e) {
        console.error(e);
        dispatch(setSnackbar({type: 'error', message: 'There was a problem while downloading data.'}));
        return {
            data: [],
            page,
            pages: 0,
        };
    }
};

const _checkEscalation = (bed, escalation) => {
    let check = true;
    if (escalation) {
        switch (escalation.level) {
            case 'core':
                check = bed?.subtype === 'core';
                break;
            case 'planned':
                check = bed?.subtype === 'core' || bed?.subtype === 'planned';
                break;
            case 'unplanned':
            default:
                check = true;
        }
    }
    return check;
};

const _getBedStatus = (bed, occupied, statuses, haveBr, unused) => {
    const id = bed?.__id;
    let status = 'Available';
    const isOccupied = occupied?.includes(id);
    let haveStatus = false;
    let isCleaning = false;

    if (statuses?.[id]) {
        haveStatus = true;
        isCleaning = statuses[id]?.status === 'cleaning' || statuses[id]?.status === 'cleaningRequest';
    }

    if (!unused) {
        status = 'Unused';
    }else if (isOccupied) {
        status = haveBr ? 'Reserved' : 'Occupied';
    } else if (haveStatus || isCleaning) {
        status = isCleaning ? 'Cleaning' : 'Blocked';
    } else if (haveBr) {
        status = 'Allocated';
    } else if (!bed?.available) {
        status = 'Disabled';
    }

    return status;
};

/*&&&&&&&&&&&&&&&&&&&&&&&
                        Widgets
&&&&&&&&&&&&&&&&&&&&&&&*/

export const prepareWidgets = (preferences) => async (dispatch) => {
    const mapped = mapWidgetsFromAPI(preferences);
    const sortedBySize = _.sortBy(mapped, wid => -SIZE_MAP[wid.size]);
    dispatch(setBMWidgets(sortedBySize));
};

export const saveWidgetPrefs = (widgetsToSave) => async (dispatch, getState) => {
    const widgetsClone = _.cloneDeep(widgetsToSave);
    const mappedToAPI = mapWidgetsToAPI(widgetsClone);
    const bmPreferences = getState().preferences?.agyle?.bedManagement;
    try {
        let prefObj = {
            bedManagement: {
                ...bmPreferences,
                mainScreenWidgets: mappedToAPI,
            },
        };

        for (let key in DEFAULT_BM_PREFS) {
            if (prefObj.bedManagement?.[key] === undefined) {
                prefObj.bedManagement[key] = DEFAULT_BM_PREFS[key];
            }
        }

        dispatch(saveAgylePreference(prefObj));
    } catch (e) {
        dispatch(setSnackbar({type: 'error', message: 'There was a problem with saving widgets preferences.'}));
    } finally {
        dispatch(setFlag('isSavingWidgetPrefs', false));
        dispatch(setEditableWidgets(!getState().bedManagement.editableWidgets));
    }
};

export default bedManagement.reducer;
